import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ProfileForm from './ProfileForm';
import TokenEntryForm from './TokenEntryForm';
import OrganizationSetupForm from './OrganizationSetupForm';
import { useAppState } from '../../hooks/useAppState';
import { useUser } from '../../context/UserContext';
import {
  setOnboardingStatus,
  setProfileData,
  setRedeemedToken,
  setSelectedLocation,
  setSelectedOrganization,
  ONBOARDING_STATUS,
} from '../../store/slices/appSlice';

/**
 * Welcome - Multi-step onboarding flow
 *
 * Flow:
 * 1. PROFILE_PENDING → ProfileForm (personal info)
 * 2. TOKEN_PENDING → TokenEntryForm (enter ADM- or INV- token)
 * 3. ORG_SETUP → OrganizationSetupForm (for admin tokens only)
 * 4. COMPLETE → Redirect to dashboard
 *
 * If user enters an INV- (invitation) token, they skip ORG_SETUP and go directly to COMPLETE.
 */
const Welcome = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Get state from centralized hook
  const { onboardingStatus, country, redeemedToken, ONBOARDING_STATUS: STATUS } = useAppState();
  const { refreshUserData } = useUser();

  // Step titles for progress indicator
  const getStepInfo = () => {
    switch (onboardingStatus) {
      case STATUS.PROFILE_PENDING:
        return { step: 1, title: 'Personal Information', subtitle: 'Tell us about yourself' };
      case STATUS.TOKEN_PENDING:
        return { step: 2, title: 'Access Code', subtitle: 'Enter your license or invitation code' };
      case STATUS.ORG_SETUP:
        return { step: 3, title: 'Organization Setup', subtitle: 'Create your organization and first store' };
      default:
        return { step: 1, title: 'Welcome', subtitle: '' };
    }
  };

  const stepInfo = getStepInfo();

  // Handler: Profile form submitted
  const handleProfileSubmit = (profileCountry) => {
    dispatch(
      setProfileData({
        onboardingStatus: ONBOARDING_STATUS.TOKEN_PENDING,
        country: profileCountry,
      })
    );
  };

  // Handler: Token redeemed
  const handleTokenRedeemed = async (tokenData) => {
    console.log('[Welcome] Token redeemed:', tokenData);

    // Store token data
    dispatch(setRedeemedToken(tokenData));

    if (tokenData.type === 'admin_license') {
      // Admin token: proceed to org setup
      dispatch(setOnboardingStatus(ONBOARDING_STATUS.ORG_SETUP));
    } else if (tokenData.type === 'invitation') {
      // Invitation token: user is now part of an organization
      // Set the organization and location from invitation
      if (tokenData.organizationId) {
        dispatch(setSelectedOrganization(tokenData.organizationId));
      }
      if (tokenData.locations && tokenData.locations.length > 0) {
        const firstLocationId = tokenData.locations[0]._id || tokenData.locations[0].id;
        dispatch(setSelectedLocation(firstLocationId));
      }

      // Mark as complete and redirect
      dispatch(
        setProfileData({
          onboardingStatus: ONBOARDING_STATUS.COMPLETE,
          organizationId: tokenData.organizationId,
        })
      );
      // Refresh UserContext so sidebar picks up the new role
      await refreshUserData();
      navigate('/dashboard');
    }
  };

  // Handler: Organization setup complete
  const handleOrgSetupComplete = async (organizationId, locationId) => {
    console.log('[Welcome] Org setup complete:', { organizationId, locationId });

    // Update state
    dispatch(setSelectedOrganization(organizationId));
    dispatch(setSelectedLocation(locationId));
    dispatch(
      setProfileData({
        onboardingStatus: ONBOARDING_STATUS.COMPLETE,
        organizationId: organizationId,
      })
    );

    // Refresh UserContext so sidebar picks up the new role (admin)
    await refreshUserData();

    // Redirect to dashboard
    navigate('/dashboard');
  };

  // Render current step
  const renderCurrentStep = () => {
    switch (onboardingStatus) {
      case STATUS.PROFILE_PENDING:
        return <ProfileForm onProfileSubmit={handleProfileSubmit} />;

      case STATUS.TOKEN_PENDING:
        return <TokenEntryForm onTokenRedeemed={handleTokenRedeemed} />;

      case STATUS.ORG_SETUP:
        return (
          <OrganizationSetupForm
            userCountry={country}
            maxLocations={redeemedToken?.maxLocations}
            token={redeemedToken?.token}
            onSetupComplete={handleOrgSetupComplete}
          />
        );

      default:
        // If complete, should have redirected, but show loading state just in case
        return <div>Redirecting...</div>;
    }
  };

  // Progress steps for visual indicator
  const progressSteps = [
    { num: 1, label: 'Profile' },
    { num: 2, label: 'Access Code' },
    { num: 3, label: 'Organization' },
  ];

  return (
    <>
      <style>{`
        .welcome-page {
          min-height: 100vh;
          background: linear-gradient(180deg, #f9fafb 0%, #fff 100%);
          padding: 120px 20px 60px;
        }

        .welcome-container {
          max-width: 800px;
          margin: 0 auto;
        }

        .welcome-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .welcome-title {
          font-size: 28px;
          font-weight: 700;
          color: #111827;
          margin: 0 0 8px 0;
        }

        .welcome-subtitle {
          font-size: 16px;
          color: #6b7280;
          margin: 0;
        }

        .progress-indicator {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 8px;
          margin-bottom: 40px;
        }

        .progress-step {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .progress-circle {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .progress-circle.completed {
          background: #10b981;
          color: white;
        }

        .progress-circle.active {
          background: #ff8264;
          color: white;
          box-shadow: 0 0 0 4px rgba(255, 130, 100, 0.2);
        }

        .progress-circle.upcoming {
          background: #e5e7eb;
          color: #9ca3af;
        }

        .progress-label {
          font-size: 13px;
          font-weight: 500;
          color: #6b7280;
        }

        .progress-label.active {
          color: #111827;
        }

        .progress-connector {
          width: 40px;
          height: 2px;
          background: #e5e7eb;
        }

        .progress-connector.completed {
          background: #10b981;
        }

        @media (max-width: 600px) {
          .progress-indicator {
            gap: 4px;
          }
          .progress-connector {
            width: 20px;
          }
          .progress-label {
            display: none;
          }
        }

        .welcome-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          padding: 40px 32px;
        }

        @media (max-width: 600px) {
          .welcome-page {
            padding: 24px 16px 40px;
          }
          .welcome-card {
            padding: 24px 16px;
          }
          .welcome-title {
            font-size: 24px;
          }
        }
      `}</style>

      <div className="welcome-page">
        <div className="welcome-container">
          {/* Header */}
          <div className="welcome-header">
            <h1 className="welcome-title">{stepInfo.title}</h1>
            <p className="welcome-subtitle">{stepInfo.subtitle}</p>
          </div>

          {/* Progress Indicator */}
          <div className="progress-indicator">
            {progressSteps.map((step, index) => {
              const isCompleted = stepInfo.step > step.num;
              const isActive = stepInfo.step === step.num;
              // For invitation flow, step 3 might be skipped
              const shouldShow = !(onboardingStatus === STATUS.TOKEN_PENDING && step.num === 3 && redeemedToken?.type === 'invitation');

              return (
                <React.Fragment key={step.num}>
                  {index > 0 && (
                    <div className={`progress-connector ${isCompleted ? 'completed' : ''}`} />
                  )}
                  <div className="progress-step">
                    <div
                      className={`progress-circle ${
                        isCompleted ? 'completed' : isActive ? 'active' : 'upcoming'
                      }`}
                    >
                      {isCompleted ? (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      ) : (
                        step.num
                      )}
                    </div>
                    <span className={`progress-label ${isActive ? 'active' : ''}`}>
                      {step.label}
                    </span>
                  </div>
                </React.Fragment>
              );
            })}
          </div>

          {/* Form Card */}
          <div className="welcome-card">
            {renderCurrentStep()}
          </div>
        </div>
      </div>
    </>
  );
};

export default Welcome;
